<script lang="ts" setup>
import {
  RelativeMoneyFormatter,
  BracketType,
} from '@apps/main/new-desktop/core/components/formatters';
import { useBrowserStore } from '@apps/main/new-desktop/core/stores';

import type { FerryTicketCategory } from '@libs/trip-planner-interfaces';
import { AppSvgIcon, AppTooltip, AppCircleCheckbox } from '@libs/ui';

interface FerryCardTicketProps {
  baseTicketPrice: number;
  ticket: FerryTicketCategory;
  selectedTicketType: string; // props.productCardContent.upsell.ticket.type
}
const props = defineProps<FerryCardTicketProps>();

interface FerryCardTicketEmits {
  (eventName: 'select-ticket-category', category: string): void;
}

const emit = defineEmits<FerryCardTicketEmits>();

const browserStore = useBrowserStore();
</script>

<template>
  <div
    class="ferry-card-ticket"
    :class="{
      flexi: ticket.type === 'FLEXI',
    }"
    @click="emit('select-ticket-category', ticket.type)"
  >
    <div class="ticket-radio">
      <AppCircleCheckbox
        :checked="props.selectedTicketType === ticket.type"
        theme="radio"
      />
    </div>

    <div class="ticket-type">
      <span>
        {{ ticket.type }}
      </span>
      <template
        v-if="
          ticket.price.amount &&
          props.baseTicketPrice &&
          ticket.price.amount !== props.baseTicketPrice
        "
      >
        <RelativeMoneyFormatter
          :data="{
            price: ticket.price.amount,
            basePrice: props.baseTicketPrice,
            color: 'color-secondary-40',
            font: 'font-label-l-light',
            bracketType: BracketType.None,
          }"
        />
      </template>
      <template v-else>
        <span class="included">{{ $t('planner.included') }}</span>
      </template>
    </div>

    <div class="ticket-info">
      <AppTooltip
        :position="
          browserStore.isSmallerThan1200px ? 'bottom-left' : 'bottom-right'
        "
      >
        <template #trigger>
          <AppSvgIcon name="infoCircle" size="18" />
        </template>
        <template #default>
          {{ ticket.description }}
        </template>
      </AppTooltip>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.ferry-card-ticket {
  --color-selected-ticket: #f2faf4;
  display: flex;
  gap: var(--spacing-12);
  padding: var(--spacing-12);
  align-items: center;
  justify-content: space-between;
  border-radius: var(--border-radius-8);
  cursor: pointer;
  &.flexi {
    background: var(--color-selected-ticket);
  }

  .ticket-type {
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
    gap: var(--spacing-4);
    @include font-label-s-light;
    color: var(--color-secondary-60);

    .included {
      color: var(--color-secondary-40);
      @include font-label-l-bold;
    }
  }
}
</style>
